/**
 * A constant pool of point lights for muzzle flashes and explosions. Adding or removing a light
 * recompiles every lit pipeline on this renderer, so the three lights live in the scene from the
 * first frame at zero intensity and are only ever moved and brightened.
 */
import { PointLight, type Scene } from 'three/webgpu';

const DECAY_PER_SECOND = 14;

export class LightPool {
  private readonly lights: PointLight[];

  constructor(scene: Scene, count = 3) {
    this.lights = Array.from({ length: count }, () => {
      const light = new PointLight(0xffb070, 0, 28, 2);
      light.castShadow = false;
      scene.add(light);
      return light;
    });
  }

  flash(x: number, y: number, z: number, hex: number, intensity: number): void {
    const light = this.lights.reduce((a, b) => (a.intensity <= b.intensity ? a : b));
    light.position.set(x, y, z);
    light.color.setHex(hex);
    light.intensity = Math.max(light.intensity, intensity);
  }

  update(dt: number): void {
    const k = Math.exp(-DECAY_PER_SECOND * dt);
    for (const light of this.lights) {
      if (light.intensity > 0.01) light.intensity *= k;
      else light.intensity = 0;
    }
  }

  dispose(): void {
    for (const light of this.lights) {
      light.removeFromParent();
      light.dispose();
    }
  }
}
